import { ChangeDetectionStrategy, Component, Input, inject } from '@angular/core';
import { BasketItemComponentDef } from '@df/basket/view/basket-item.component.def';
import { Status } from '@df/core/status';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import { TranslationService } from '@df/ui/translation/translation.service';
import { BranchService } from '../../../branch/branch.service';
import { NCmsBranchCollection } from '../../../branch/collection/branch-collection.component';
import { NCmsDelivery } from '../../../common/delivery/modal/delivery-modal.component';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.Basket {
        interface BasketItem {
            is_gift?: boolean;
            has_gift?: boolean;
        }
    }
}

@Component({
    selector: 'basket-view-item',
    templateUrl: './basket-view-item.html',
    styleUrls: ['./basket-view-item.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BasketViewItemComponent extends BasketItemComponentDef {
    /**
     * If qty should be hidden
     */
    @Input()
    hideQty = false;

    /**
     * If using minibasket context
     */
    @Input({
        required: true
    })
    context?: 'basket' | 'minibasket' | 'checkout' | 'notice';

    /**
     * Collection/delivery availability
     */
    private collectionAvailable: boolean | 'noselection' = false;
    private deliveryAvailable: boolean | 'noselection' = false;

    /**
     * Services
     */
    protected swapStatus = new Status();
    private translationService = inject(TranslationService);
    private branchService = inject(BranchService);
    private dfModalService = inject(DfModalService);

    /**
     * Stock warning
     */
    get warning(): string | undefined {
        if (!this.isSalable) {
            return this.maxQty
                ? this.translationService.transform('Only % availble', this.maxQty)
                : this.translationService.transform('Out of stock');
        }
        return undefined;
    }

    /**
     * Collection/delivery availability callback
     */
    protected setAvailble(area: 'collection' | 'delivery', available: boolean | 'noselection'): void {
        if (area === 'collection') {
            if (this.collectionAvailable !== available) {
                this.collectionAvailable = available;
                this.detectChanges();
            }
        } else {
            if (this.deliveryAvailable !== available) {
                this.deliveryAvailable = available;
                this.detectChanges();
            }
        }
    }

    /**
     * If can swap item between collection and delivery
     */
    protected get canSwap(): boolean {
        if (this.context !== 'basket') {
            return false;
        }
        return this.item?.data?.isCollection ? !!this.deliveryAvailable : !!this.collectionAvailable;
    }

    /**
     * Swap item between collection and delivery
     */
    protected async swap(): Promise<void> {
        if (!this.canSwap) {
            return;
        }
        const item = this.item!;
        const toCollection = item.data.isCollection === 'false' || !item.data.isCollection;

        const next = () => {
            const isAvailble = toCollection ? this.collectionAvailable : this.deliveryAvailable;
            if (isAvailble !== true) {
                return;
            }

            item.changeRequest(this.swapStatus, {
                ...item.data.request,
                ...(toCollection
                    ? {
                          isCollection: 'true',
                          branchNumber: this.branchService.branchNumber()
                      }
                    : {
                          isCollection: 'false',
                          branchNumber: -1
                      })
            });
            this.detectChanges();
        };

        if (toCollection) {
            if (this.collectionAvailable === 'noselection') {
                const data: NCmsBranchCollection.IModalData = {
                    product: this.product!
                };
                this.dfModalService.open(this.swapStatus, NCmsBranchCollection.MODAL_ID, undefined, data, modalRef => {
                    // if we aim to submit after branch is set we need to add callback on modal closed
                    modalRef?.afterClosed().subscribe(() => setTimeout(next));
                });
            } else {
                next();
            }
        } else {
            if (this.deliveryAvailable === 'noselection') {
                const data: NCmsDelivery.IModalData = {
                    productId: this.product!.id
                };
                this.dfModalService.open(this.swapStatus, NCmsDelivery.MODAL_ID, undefined, data, modalRef => {
                    // if we aim to submit after branch is set we need to add callback on modal closed
                    modalRef?.afterClosed().subscribe(() => setTimeout(next));
                });
            } else {
                next();
            }
        }
    }
}
